import { Label, SelectedFile, SelectedFileStatus, Text, TextColor, TextSize } from '@vizit/atoms';
import { DropZone } from '@vizit/molecules';
import * as React from 'react';
import { type Control, type FieldValues, type Path, useController } from 'react-hook-form';

interface FormDropZoneProps<T extends FieldValues>
  extends Omit<
    React.ComponentProps<typeof DropZone.Provider>,
    'allowsMultiple' | 'maxFiles' | 'onFilesChange' | 'errors' | 'children'
  > {
  /**
   * Whether the dropzone allows multiple files.
   *
   * @optional
   * @default false
   * @type boolean
   */
  allowsMultiple?: boolean;
  /**
   * The control object from the `useForm` hook.
   */
  control: Control<T>;
  /**
   * The name of the field.
   */
  name: Path<T>;
  /**
   * The error message to display when the file is not uploaded.
   */
  uploadStatus?: SelectedFileStatus;
  /**
   * The label to display above the dropzone.
   */
  label?: string;
  /**
   * Whether the dropzone is required.
   */
  isRequired?: boolean;
  /**
   * The helper text to display below the dropzone.
   */
  helperText?: string;
  /**
   * The function to call when an error occurs within the dropzone.
   * This will let the form know that there is an error and the submit button will be disabled.
   *
   * @example
   * ```tsx
   * const { setError } = useForm<FormType>();
   *
   * const onFileError = (error?: string) => {
   *   setError('file', { type: 'custom', message: error });
   * };
   * ```
   */
  onFileValidationError?: (error?: string) => void;
  /**
   * Callback function to handle the file status change
   * @param status - The status of the file
   */
  onFileStatusChange?: (status: SelectedFileStatus) => void;
  /**
   * Additional CSS classes to apply to the inner DropZone container.
   */
  dropZoneClassName?: string;
  /**
   * Additional CSS classes to apply to the SelectedFiles container.
   */
  selectedFilesClassName?: string;
  /**
   * Test ID for E2E tests
   */
  'data-testid'?: string;
}

function _FormDropZone<T extends FieldValues>(props: FormDropZoneProps<T>, ref: React.Ref<HTMLDivElement>) {
  const {
    allowsMultiple = false,
    control,
    name,
    uploadStatus,
    label,
    isRequired,
    helperText,
    onFileValidationError,
    onFileStatusChange,
    dropZoneClassName,
    selectedFilesClassName,
    'data-testid': testId,
    ...other
  } = props;

  const labelId = React.useId();
  const helperTextId = React.useId();

  const { field, formState } = useController({ name, control });

  const defaultValue: File[] | undefined = React.useMemo(() => {
    if (!formState?.defaultValues?.[name]) {
      return undefined;
    }

    if (allowsMultiple) {
      return formState?.defaultValues?.[name];
    }

    return [formState?.defaultValues?.[name]];
  }, [formState?.defaultValues, name, allowsMultiple]);

  const handleOnFilesChange = (files: File[]) => {
    if (!allowsMultiple) {
      const file = files[0];
      field.onChange(file);
      return;
    }

    field.onChange(files);
  };

  const handleOnRemove = (file: File, onRemove: (file: File) => void) => {
    onRemove(file);

    // Reset the file status
    onFileStatusChange?.(SelectedFileStatus.Ready);
  };

  return (
    <div ref={ref} className="flex flex-col" data-testid={testId}>
      {label && (
        <Label id={labelId} isRequired={isRequired} className="mb-4">
          {label}
        </Label>
      )}
      <DropZone.Provider
        allowsMultiple={allowsMultiple}
        defaultFiles={defaultValue}
        onFilesChange={handleOnFilesChange}
        onFileValidationError={onFileValidationError}
        {...other}
      >
        <DropZone
          className={dropZoneClassName}
          aria-labelledby={label ? labelId : undefined}
          aria-describedby={helperText ? helperTextId : undefined}
        />
        <DropZone.SelectedFiles
          className={selectedFilesClassName}
          aria-labelledby={label ? labelId : undefined}
          aria-describedby={helperText ? helperTextId : undefined}
        >
          {({ file, onRemove, className }) => (
            <SelectedFile
              file={file}
              status={uploadStatus}
              onRemove={(file) => handleOnRemove(file, onRemove)}
              className={className}
            />
          )}
        </DropZone.SelectedFiles>
      </DropZone.Provider>
      {helperText && (
        <Text id={helperTextId} color={TextColor.Tertiary} size={TextSize.Sm} className="mt-4">
          {helperText}
        </Text>
      )}
    </div>
  );
}

const FormDropZone = React.forwardRef(_FormDropZone) as <T extends FieldValues>(
  props: FormDropZoneProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => ReturnType<typeof _FormDropZone>;

export { FormDropZone };
export type { FormDropZoneProps };
